import { useQuery } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, FileWarning, Package, MapPin } from 'lucide-react';
import { fetchComplaintStats } from '@/core/api';
import { useFactory } from '@/core/hooks/useFactory';
import { useTenant } from '@/core/hooks/useTenant';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Badge } from '@/shared/ui/badge';

/**
 * Dashboard showing complaint KPIs and statistics.
 */
export const ComplaintsDashboard = () => {
  const { selectedFactoryId } = useFactory();
  const { selectedTenantId } = useTenant();

  const { data: stats, isLoading } = useQuery({
    queryKey: ['complaint-stats', selectedTenantId, selectedFactoryId],
    queryFn: () => {
      if (!selectedFactoryId) return null;
      return fetchComplaintStats(selectedFactoryId);
    },
    enabled: !!selectedFactoryId,
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Today Count */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Complaints Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold">{stats.today}</span>
              <FileWarning className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        {/* This Week Count */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              This Week
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold">{stats.thisWeek}</span>
              <TrendingUp className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        {/* Open Complaints */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Open Complaints
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold">
                {(stats.byStatus.draft || 0) +
                  (stats.byStatus.complaint_recorded || 0) +
                  (stats.byStatus.in_progress || 0)}
              </span>
              <FileWarning className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        {/* Closed This Week */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Closed This Week
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold">{stats.byStatus.closed || 0}</span>
              <TrendingDown className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Articles */}
      {stats.topArticles.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="h-5 w-5" />
              Most Complained Articles (This Week)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.topArticles.slice(0, 5).map((article) => (
                <div
                  key={article.article_id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                >
                  <div className="flex items-center gap-3">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{article.article_code}</p>
                      {article.article_name && (
                        <p className="text-sm text-muted-foreground">{article.article_name}</p>
                      )}
                    </div>
                  </div>
                  <Badge variant="destructive">{article.count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* By Area */}
      {stats.byArea.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Complaints by Area (This Week)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.byArea.map((area) => (
                <div
                  key={area.area_id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                >
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <p className="font-medium">{area.area_name}</p>
                  </div>
                  <Badge variant="outline">{area.count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
